import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin') {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/admin';
    }
    return Promise.reject(error);
  }
);

// Admin Auth
export const adminLogin = (credentials) => api.post('/admin/login', credentials);
export const verifyAdmin = () => api.get('/admin/verify');

// User Management
export const getUsers = () => api.get('/admin/users');
export const createUser = (data) => api.post('/admin/users', data);
export const updateUser = (username, data) => api.put(`/admin/users/${username}`, data);
export const deleteUser = (username) => api.delete(`/admin/users/${username}`);

// Categories
export const getCategories = () => api.get('/categories');
export const createCategory = (data) => api.post('/admin/categories', data);
export const updateCategory = (id, data) => api.put(`/admin/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/admin/categories/${id}`);

// Subcategories
export const getSubcategories = (categoryId) => api.get(`/subcategories/${categoryId}`);
export const createSubcategory = (data) => api.post('/admin/subcategories', data);
export const updateSubcategory = (id, data) => api.put(`/admin/subcategories/${id}`, data);
export const deleteSubcategory = (id) => api.delete(`/admin/subcategories/${id}`);

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getAdminProducts = (params) => api.get('/admin/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const getSearchSuggestions = (q) => api.get('/search/suggestions', { params: { q } });
export const createProduct = (data) => api.post('/admin/products', data);
export const updateProduct = (id, data) => api.put(`/admin/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/admin/products/${id}`);

// File Upload
export const uploadFile = (file, type) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/admin/upload/${type}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const uploadSubcategoryIcon = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/upload/subcategory-icon', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Banners
export const getBanners = () => api.get('/banners');
export const getAdminBanners = () => api.get('/admin/banners');
export const createBanner = (data) => api.post('/admin/banners', data);
export const updateBanner = (id, data) => api.put(`/admin/banners/${id}`, data);
export const deleteBanner = (id) => api.delete(`/admin/banners/${id}`);

// Clients
export const getClients = () => api.get('/clients');
export const getAdminClients = () => api.get('/admin/clients');
export const createClient = (data) => api.post('/admin/clients', data);
export const updateClient = (id, data) => api.put(`/admin/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/admin/clients/${id}`);

// Enquiries
export const submitEnquiry = (data) => api.post('/enquiries', data);
export const getEnquiries = (params) => api.get('/admin/enquiries', { params });
export const updateEnquiryStatus = (id, status) => api.put(`/admin/enquiries/${id}/status`, null, { params: { status } });

// Dealership
export const submitDealership = (data) => api.post('/dealership', data);
export const getDealerships = (params) => api.get('/admin/dealership', { params });
export const updateDealershipStatus = (id, status) => api.put(`/admin/dealership/${id}/status`, null, { params: { status } });

// Events
export const getEvents = (params) => api.get('/events', { params });
export const getEvent = (id) => api.get(`/events/${id}`);
export const createEvent = (data) => api.post('/admin/events', data);
export const updateEvent = (id, data) => api.put(`/admin/events/${id}`, data);
export const deleteEvent = (id) => api.delete(`/admin/events/${id}`);

// Dashboard Stats
export const getDashboardStats = () => api.get('/admin/stats');
export const getAnalytics = (days = 30) => api.get(`/admin/analytics?days=${days}`);

// Notification Settings
export const getNotificationSettings = () => api.get('/admin/settings/notifications');
export const updateNotificationSettings = (data) => api.post('/admin/settings/notifications', data);
export const testEmailNotification = () => api.post('/admin/settings/test-email');
export const testWhatsappNotification = () => api.post('/admin/settings/test-whatsapp');

// Universal Catalog
export const getUniversalCatalog = () => api.get('/settings/universal-catalog');
export const updateUniversalCatalog = (catalog_url) => api.post('/admin/settings/universal-catalog', { catalog_url });

// Location Settings
export const getLocationSettings = () => api.get('/settings/location');
export const updateLocationSettings = (data) => api.post('/admin/settings/location', data);

// Change Password & Profile
export const changePassword = (data) => api.post('/admin/change-password', data);
export const updateProfile = (data) => api.put('/admin/profile', data);

// Reviews
export const getReviews = () => api.get('/reviews');
export const submitReview = (data) => api.post('/reviews/submit', data);
export const getAdminReviews = (status) => api.get('/admin/reviews', { params: status ? { status } : {} });
export const createReview = (data) => api.post('/admin/reviews', data);
export const updateReview = (id, data) => api.put(`/admin/reviews/${id}`, data);
export const deleteReview = (id) => api.delete(`/admin/reviews/${id}`);

// Festival
export const getFestival = () => api.get('/settings/festival');
export const updateFestival = (data) => api.post('/admin/settings/festival', data);

// Careers
export const getCareers = () => api.get('/careers');
export const getAdminCareers = () => api.get('/admin/careers');
export const createCareer = (data) => api.post('/admin/careers', data);
export const updateCareer = (id, data) => api.put(`/admin/careers/${id}`, data);
export const deleteCareer = (id) => api.delete(`/admin/careers/${id}`);

// Case Studies
export const getCaseStudies = (category) => api.get('/case-studies', { params: category ? { category } : {} });
export const getCaseStudy = (id) => api.get(`/case-studies/${id}`);
export const getCaseStudyCategories = () => api.get('/case-studies-categories');
export const getAdminCaseStudies = () => api.get('/admin/case-studies');
export const createCaseStudy = (data) => api.post('/admin/case-studies', data);
export const updateCaseStudy = (id, data) => api.put(`/admin/case-studies/${id}`, data);
export const deleteCaseStudy = (id) => api.delete(`/admin/case-studies/${id}`);

// Bulk Upload
export const downloadBulkTemplate = () => api.get('/admin/bulk-upload/template', { responseType: 'blob' });
export const bulkUploadProducts = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/admin/bulk-upload/products', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export default api;
